import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TableColorConfig } from "../../../models/workPaperTemplate";

// Default colors matching backend defaults
const DEFAULT_COLORS: Record<keyof NonNullable<TableColorConfig>, string> = {
  divider: "000000",
  sampleHeader: "808080",
  testHeader: "136082",
  sampleColumnHeader: "D0D0D0",
  testColumnHeader: "BFDBFE",
  passFill: "DCFCE7",
  failFill: "FECACA",
};

const COLOR_LABELS: Record<keyof NonNullable<TableColorConfig>, string> = {
  divider: "Divider",
  sampleHeader: "Sample Details Header",
  testHeader: "Test Details Header",
  sampleColumnHeader: "Sample Column Headers",
  testColumnHeader: "Test Column Headers",
  passFill: "Pass Fill",
  failFill: "Fail Fill",
};

export interface DetailTestingTableFieldFormData {
  sheetNamingMode: "multi" | "single";
  sheetNamePrefix: string;
  sheetName?: string;
  sheetNameTemplate?: string;
  startRow?: number;
  headerRow?: number;
  columnStart?: string;
  includeSampleAttributes?: boolean;
  includeProcedureIds?: string[] | null;
  colors?: TableColorConfig | null;
}

interface DetailTestingTableFieldFormProps {
  value: DetailTestingTableFieldFormData;
  onChange: (value: DetailTestingTableFieldFormData) => void;
  readOnly?: boolean;
}

export function DetailTestingTableFieldForm({
  value,
  onChange,
  readOnly = false,
}: DetailTestingTableFieldFormProps) {
  const [formData, setFormData] =
    useState<DetailTestingTableFieldFormData>(value);
  const [showColors, setShowColors] = useState(false);

  useEffect(() => {
    // Derive sheetNamingMode from existing data if not set
    const mode =
      value.sheetNamingMode ?? (value.sheetName ? "single" : "multi");
    setFormData({ ...value, sheetNamingMode: mode });
  }, [value]);

  const handleChange = (
    field: keyof DetailTestingTableFieldFormData,
    newValue: string | number | boolean | string[] | null | undefined,
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  const handleModeChange = (mode: "multi" | "single") => {
    const updated: DetailTestingTableFieldFormData = {
      ...formData,
      sheetNamingMode: mode,
      // Clear the opposite field when switching modes
      sheetName: mode === "single" ? formData.sheetName || "" : undefined,
      sheetNamePrefix:
        mode === "multi" ? formData.sheetNamePrefix || "Testing" : "",
    };
    setFormData(updated);
    onChange(updated);
  };

  const handleColorChange = (
    colorKey: keyof NonNullable<TableColorConfig>,
    colorValue: string,
  ) => {
    // Sanitize input: uppercase, only hex chars, max 6 chars
    const sanitized = colorValue
      .toUpperCase()
      .replace(/[^0-9A-F]/g, "")
      .slice(0, 6);
    const updatedColors: TableColorConfig = {
      ...(formData.colors || {}),
      [colorKey]: sanitized || undefined,
    };
    // If all colors are undefined, set colors to null
    const hasAnyColor = Object.values(updatedColors).some(
      (v) => v !== undefined,
    );
    const updated: DetailTestingTableFieldFormData = {
      ...formData,
      colors: hasAnyColor ? updatedColors : null,
    };
    setFormData(updated);
    onChange(updated);
  };

  const colorKeys = Object.keys(DEFAULT_COLORS) as Array<
    keyof NonNullable<TableColorConfig>
  >;

  return (
    <div className="space-y-4">
      {/* Sheet Naming Mode Radio Toggle */}
      <div className="space-y-3">
        <Label>Sheet Mode</Label>
        <RadioGroup
          value={formData.sheetNamingMode}
          onValueChange={(val) => handleModeChange(val as "multi" | "single")}
          disabled={readOnly}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="multi" id="mode-multi" />
            <Label htmlFor="mode-multi" className="cursor-pointer font-normal">
              Create new sheets (one per procedure)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="single" id="mode-single" />
            <Label htmlFor="mode-single" className="cursor-pointer font-normal">
              Use existing sheet
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Conditional Sheet Name Input */}
      {formData.sheetNamingMode === "multi" ? (
        <div className="space-y-2">
          <Label htmlFor="sheetNamePrefix">Sheet Name Prefix</Label>
          <Input
            id="sheetNamePrefix"
            value={formData.sheetNamePrefix}
            onChange={(e) => handleChange("sheetNamePrefix", e.target.value)}
            placeholder="Testing"
            disabled={readOnly}
          />
          <p className="text-sm text-muted-foreground">
            Prefix for generated sheet names (e.g., &quot;Testing&quot; creates
            &quot;Testing_ProcedureName&quot;)
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="sheetName">Sheet Name</Label>
          <Input
            id="sheetName"
            value={formData.sheetName || ""}
            onChange={(e) => handleChange("sheetName", e.target.value)}
            placeholder="TestResults"
            disabled={readOnly}
          />
          <p className="text-sm text-muted-foreground">
            Exact name of the sheet to use for all testing data
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="headerRow">Header Row</Label>
          <Input
            id="headerRow"
            type="number"
            min={1}
            value={formData.headerRow ?? 1}
            onChange={(e) =>
              handleChange("headerRow", parseInt(e.target.value, 10) || 1)
            }
            placeholder="1"
            disabled={readOnly}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="startRow">Data Starting Row</Label>
          <Input
            id="startRow"
            type="number"
            min={1}
            value={formData.startRow ?? 2}
            onChange={(e) =>
              handleChange("startRow", parseInt(e.target.value, 10) || 2)
            }
            placeholder="2"
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="columnStart">Starting Column</Label>
        <Input
          id="columnStart"
          value={formData.columnStart ?? "A"}
          onChange={(e) =>
            handleChange("columnStart", e.target.value.toUpperCase())
          }
          placeholder="A"
          disabled={readOnly}
          className="uppercase"
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="includeSampleAttributes"
          checked={formData.includeSampleAttributes ?? true}
          onCheckedChange={(checked) =>
            handleChange("includeSampleAttributes", checked === true)
          }
          disabled={readOnly}
        />
        <Label htmlFor="includeSampleAttributes" className="cursor-pointer">
          Include sample identifying attributes (Transaction ID, Date, etc.)
        </Label>
      </div>

      {/* Custom Colors Collapsible Section */}
      <Collapsible
        open={showColors}
        onOpenChange={setShowColors}
        className="rounded-md border"
      >
        <CollapsibleTrigger className="flex w-full items-center justify-between p-3 hover:bg-stone-50">
          <span className="text-sm font-medium">Custom Colors (Optional)</span>
          {showColors ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 p-3 pt-0">
          <p className="mb-3 text-xs text-muted-foreground">
            Leave blank to use default colors. Enter 6-character hex values
            (e.g., FF5733).
          </p>
          <div className="grid grid-cols-2 gap-3">
            {colorKeys.map((colorKey) => (
              <div key={colorKey} className="space-y-1">
                <Label htmlFor={`color-${colorKey}`} className="text-xs">
                  {COLOR_LABELS[colorKey]}
                </Label>
                <div className="flex items-center gap-2">
                  {/* Color swatch preview */}
                  <div
                    className="h-6 w-6 flex-shrink-0 rounded border border-stone-300"
                    style={{
                      backgroundColor: `#${formData.colors?.[colorKey] || DEFAULT_COLORS[colorKey]}`,
                    }}
                  />
                  <Input
                    id={`color-${colorKey}`}
                    value={formData.colors?.[colorKey] || ""}
                    onChange={(e) =>
                      handleColorChange(colorKey, e.target.value)
                    }
                    placeholder={DEFAULT_COLORS[colorKey]}
                    disabled={readOnly}
                    className="h-8 font-mono text-xs uppercase"
                    maxLength={6}
                  />
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Preview Section */}
      <div className="rounded-md border border-purple-200 bg-purple-50 p-3 text-sm">
        <strong>Preview:</strong>{" "}
        {formData.sheetNamingMode === "multi" ? (
          <>
            {formData.sheetNamePrefix || "Prefix"}_[ProcedureName] - One sheet
            per procedure
          </>
        ) : (
          <>
            {formData.sheetName || "SheetName"} - Single sheet for all
            procedures
          </>
        )}
        <div className="mt-2 text-xs">
          Header Row: {formData.headerRow ?? 1} | Data Start: Row{" "}
          {formData.startRow ?? 2}
        </div>
        <div className="mt-1 text-xs">
          Starting at column {formData.columnStart ?? "A"}
        </div>
        {formData.colors && Object.keys(formData.colors).length > 0 && (
          <div className="mt-1 text-xs text-purple-700">
            Custom colors configured
          </div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          Generates a testing table for each procedure with sample data and test
          results
        </div>
      </div>
    </div>
  );
}
